/* eslint-disable max-classes-per-file */
import React from 'react';
import { Agent, GatewayDefinition, Project, ProjectType } from '@moderepo/mode-internal-apis';
import { AppProxyAPI } from '@moderepo/mode-apis';
import { RouteGroup, SlideOutCompProps } from '@moderepo/mode-app-components';
import { SlideOutPanelOptions } from '@moderepo/mode-ui-state';
import { ProjectUIConfig } from './ritzUIExtensionUtils';


export enum DEFAULT_SUB_ROUTE_NAME {
    DEFAULT = 'default',
    DEVICES = 'devices',
    HOME_MEMBERS = 'home_members',
    DASHBOARD = 'dashboard',
    SETTINGS = 'settings',
    DATA = 'data',
    LOGS = 'logs',
    TSDB = 'tsdb',
    VIDEOS = 'videos',
    AGENTS = 'agents',
    AUDIT_LOGS = 'audit_logs'
}


/**
 * The set of route name for Ritz HIDDEN admin pages
 */
export enum DEFAULT_ADMIN_ROUTE_NAME {
    UI_SETTINGS = 'ui_settings',
}



/**
 * The interface for custom UI handler. Any project type that needs to have custom UI can implement this interface and implement the required
 * functions from this interface. Not all project type need to implement this interface, only project types that need to have a different UI
 * than the default Ritz UI.
 */
export interface ProjectTypeCustomUIHandler {
    readonly getCustomUIConfig: (loggedInAgent: Agent | undefined, project: Project | undefined)=> ProjectUIConfig;
}



/**
 * The interface for the APP proxy API for each project type. If a type of project has its own App Proxy API, it can implement this interface
 * and implement the getAppProxyAPI function. Ritz will call this function to initialize the App Proxy API with the project key when project
 * key is retrieved. Not all project has App proxy API so only projects that has app proxy API needs to implement this interface and register
 * the handler with the ProjectTypeHandlersManager.
 */
export interface ProjectTypeAppProxyHandler {
    /**
     * Return the AppProxyAPI instance that the project use.
     */
    readonly getAppProxyAPI: ()=> AppProxyAPI;

    /**
     * Fetch Gateway Definitions for the whole project. Not all project type has this feature therefore only the project type that has
     * some Gateway definition needs to implement this function. The handler can use any way to implement this function e.g. make
     * API call or return some static data.
     * NOTE: We will include this function in ProjectTypeAppProxyHandler for now since it is kind ot related to AppProxy because
     * this function might require making API call to app proxy to fetch data.
     */
    readonly getGatewayCatalog?: (projectId: number)=> Promise<readonly GatewayDefinition[]>;
}



/**
 * The interface for the route handler for a project type. Each project type will have its own set of custom routes and the components for
 * those routes. So any project type that has its own routes must implement this interface and implement the functions defined in this
 * interface and register the handler with ProjectTypeHandlersManager. Ritz will call these functions to get the routes and components for each
 * project type. If a project type does not implement this handler, it will only have the basic routes that are defined by Ritz.
 */
export interface ProjectTypeRoutesHandler {
    /**
     * Return the complete set of paths for the project, including the default paths implemented for Ritz e.g. Login, My Account, Dashboard,
     * Agents, etc... Therefore, the function usually return a MERGED object of the DEFAULT_APP_PATHS and the project specific paths
     */
    readonly getRoutePaths: (project: Project | undefined, loggedInAgent: Agent | undefined)=> {[name: string]: string | RouteGroup};

    /**
     * There should be a Route associated with EACH path returned by the getRoutePaths. This function must return the routes that the project has.
     * NOTE: this function only need to return the project SPECIFIC routes, the routes that only this project has or the routes that this
     * project will override the default route.
     * For example: RobotCloud project has a different Dashboard page and has Issues page which the default project does not have. Therefore,
     * RobotCloud will return these 2 routes from this getRoutesComponent function.
     */
    readonly getRoutesComponent: (project: Project | undefined, loggedInAgent: Agent | undefined)=> readonly JSX.Element[];
}



/**
 * The interface for defining the layout for each project type. Any project type that have its own layout can implement this interface and
 * implement the getLayoutComponent function and register the handler with ProjectTypeHandlersManager. When Ritz create a Layout
 * for a page, it will call this handler to get the component to be used for the layout. If no custom layout is provided, Ritz will use
 * the default layout component.
 */
export interface ProjectTypePageLayoutHandler {
    /**
     * Some project type has different page layout e.g. different location for the top nav, drawer menu, etc... Therefore, each project type
     * must implement this function and tell the system what type of layout it has. However, because this is optional, this function can return
     * undefined. This is OPTIONAL and only project that has custom layout need to implement this function.
     */
    readonly getPageLayoutComponent?: (project: Project | undefined, loggedInAgent: Agent | undefined)=> React.FC<{}> | undefined;


    /**
     * Some project type has different page info components. Default PageInfo component can only show MODE platform entities
     * e.g. User, Home, Device because that is all it knows. However, RobotCloud project type has Robots and SensorCloud
     * project type has SensorModules and they want to show these entity in the PageInfo component. Therefore, each project
     * type can implement this function and return the PageInfo component it wants to use instead of using the DEFAULT page
     * info component. This is optional and only needed for project that has custom page info component
     */
    readonly getPageInfoComponent?: (project: Project | undefined, loggedInAgent: Agent | undefined)=> React.FC<{}> | undefined;
}



/**
 * The Handler to handle creating the components for displaying in the Slide Out panel. Whenever a Slide Out panel is shown, this handler
 * will be called and provided with the slide out options. It is up to the handler to decide which component to created based on the
 * slide out option's compType. If the type is supported/known by the handler, the handler can create a return a component of type
 * "SlideOutPanel". If a type is not supported or known by the handler, the handler can return undefined.
 */
export interface ProjectTypeSlideOutCompHandler {
    readonly getSlideOutComponent: (options: SlideOutPanelOptions)=> React.ReactElement<SlideOutCompProps> | undefined;
}



/**
 * This SINGLETON class will manage all the project type handlers. So whenever we need to know the Route paths or the route components for
 * any given type of project, we can call this manager's getXXXHandler function and get the reference to the SPECIFIC type of ProjectTypeHandler.
 * Once we have the reference to the ProjectType Handler, we can then call it's API to get what we need.
 */
export class ProjectTypeHandlersManager {

    private static instance: ProjectTypeHandlersManager;

    public static getInstance (): ProjectTypeHandlersManager {
        if (!ProjectTypeHandlersManager.instance) {
            ProjectTypeHandlersManager.instance = new ProjectTypeHandlersManager();
        }
        return ProjectTypeHandlersManager.instance;
    }


    /** APP PROXY handlers * */
    private appProxyHandlers = new Map<ProjectType, ProjectTypeAppProxyHandler>();

    public registerAppProxyHandler (projectType: ProjectType, handler: ProjectTypeAppProxyHandler) {
        this.appProxyHandlers.set(projectType, handler);
    }

    public getAppProxyHandler (projectType: ProjectType | undefined): ProjectTypeAppProxyHandler | undefined {
        if (projectType && this.appProxyHandlers.has(projectType)) {
            return this.appProxyHandlers.get(projectType);
        }
        return this.appProxyHandlers.get(ProjectType.DEFAULT);
    }



    /** ROUTES handlers * */
    private routesHandlers = new Map<ProjectType, ProjectTypeRoutesHandler>();

    public registerRoutesHandler (projectType: ProjectType, handler: ProjectTypeRoutesHandler) {
        this.routesHandlers.set(projectType, handler);
    }

    public getRoutesHandler (projectType: ProjectType | undefined): ProjectTypeRoutesHandler | undefined {
        if (projectType && this.routesHandlers.has(projectType)) {
            return this.routesHandlers.get(projectType);
        }
        return this.routesHandlers.get(ProjectType.DEFAULT);
    }


    /** LAYOUT handlers * */
    private pageLayoutHandlers = new Map<ProjectType, ProjectTypePageLayoutHandler>();

    public registerPageLayoutHandler (projectType: ProjectType, handler: ProjectTypePageLayoutHandler) {
        this.pageLayoutHandlers.set(projectType, handler);
    }

    public getPageLayoutHandler (projectType: ProjectType | undefined): ProjectTypePageLayoutHandler | undefined {
        if (projectType && this.pageLayoutHandlers.has(projectType)) {
            return this.pageLayoutHandlers.get(projectType);
        }
        return this.pageLayoutHandlers.get(ProjectType.DEFAULT);
    }


    /** CUSTOM UI handlers * */
    private customUIHandlers = new Map<ProjectType, ProjectTypeCustomUIHandler>();

    public registerProjectCustomUIHandler (projectType: ProjectType, handler: ProjectTypeCustomUIHandler) {
        this.customUIHandlers.set(projectType, handler);
    }

    public getProjectCustomUIHandler (projectType: ProjectType | undefined): ProjectTypeCustomUIHandler | undefined {
        if (projectType && this.customUIHandlers.has(projectType)) {
            return this.customUIHandlers.get(projectType);
        }
        return this.customUIHandlers.get(ProjectType.DEFAULT);
    }


    /**
     * Slide Out component handlers
     * Unlike the other handlers, the slideOutComponentHandlers is a map by contentType, not project type.
     */
    private slideOutComponentHandlers = new Map<string, ProjectTypeSlideOutCompHandler>();

    public registerSlideOutComponentHandler (compType: string, handler: ProjectTypeSlideOutCompHandler) {
        this.slideOutComponentHandlers.set(compType, handler);
    }

    public getSlideOutPanelComponentHandler (compType: string | undefined): ProjectTypeSlideOutCompHandler | undefined {
        if (compType && this.slideOutComponentHandlers.has(compType)) {
            return this.slideOutComponentHandlers.get(compType);
        }
        return undefined;
    }
}
